import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useGoogleAuthErrorMessage } from '@/components/auth/GoogleSignInButton';
import { LogoIcon } from '@/components/ui/Logo';
import { Button } from '@/components/ui/button';
import { XCircle } from 'lucide-react';
import { BrandBar } from '@/components/ui/BrandLoader';

/**
 * Landing page for the Google sign-in round-trip.
 *
 * The API redirects here with either `?error=<code>` or `?code=<handoff>` — a
 * single-use, 2-minute code. Never a token: swapping the code over POST is what
 * establishes the session, so nothing sensitive is in the URL, the browser
 * history or a Referer header.
 *
 * On success the user is dropped into the `redirect` they came from (validated
 * server-side to be a same-origin path) or /dashboard.
 */
export default function GoogleCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const loginWithGoogleCode = useAuthStore((s) => s.loginWithGoogleCode);
  const googleErrorMessage = useGoogleAuthErrorMessage();

  const search = useSearch({ strict: false }) as { code?: string; error?: string };
  const [errorMessage, setErrorMessage] = useState('');
  // The handoff code is single-use — React 19 StrictMode double-invokes
  // effects in dev, and a second exchange would fail. Guard it.
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    if (search.error) {
      setErrorMessage(googleErrorMessage(search.error));
      return;
    }
    if (!search.code) {
      setErrorMessage(googleErrorMessage('provider_error'));
      return;
    }

    loginWithGoogleCode(search.code)
      .then(({ isNewUser, redirect }) => {
        // Drop the code from the URL before navigating away.
        window.history.replaceState(null, '', '/auth/google/callback');
        if (redirect) {
          navigate({ to: redirect, replace: true });
        } else if (isNewUser) {
          navigate({ to: '/dashboard', search: { onboarding: 'true' }, replace: true });
        } else {
          navigate({ to: '/dashboard', replace: true });
        }
      })
      .catch(() => setErrorMessage(googleErrorMessage('provider_error')));
    // Run exactly once on mount — the guard above is the real safety net.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[var(--color-base)] text-[var(--color-text)] p-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
        className="flex flex-col items-center text-center max-w-sm gap-5"
      >
        <LogoIcon size="lg" />

        {errorMessage ? (
          <>
            <XCircle className="w-10 h-10 text-[var(--color-destructive)]" />
            <h1 className="text-2xl font-semibold tracking-tight">
              {t('auth:google.errorTitle')}
            </h1>
            <p className="text-[var(--color-text-muted)]">{errorMessage}</p>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <Button variant="primary" onClick={() => navigate({ to: '/login', replace: true })}>
                {t('auth:google.backToLogin')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <BrandBar />
            <p className="text-[var(--color-text-muted)]">{t('auth:google.finishing')}</p>
          </>
        )}
      </motion.div>
    </div>
  );
}
